import { Container, makeStyles, Typography } from "@material-ui/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import LocationHeader from "components/Headers/LocationHeader";
import Stepper from "components/Stepper";
import { StepProvider } from "contexts/stepper-context";
import { createMapRent, getCheckRENTSubmission } from "crud/rent.crud";
import useStepper from "hooks/useStepper";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Redirect } from "react-router-dom/cjs/react-router-dom";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { DIALOG } from "utils/actionsConsts";
import NavigationButtons from "./navigation-buttons";
import CompanyInfoTab from "./Steps/CompanyInfo";
import ConfirmationTab from "./Steps/Confirmation";
import EmployeesInfoTab from "./Steps/EmployeesInfo";
import WorkCentersInfoTab from "./Steps/WorkCentersInfo";

const useStyles = makeStyles(() => ({
  root: {
    paddingTop: "calc(110px + 2rem)",
    paddingBottom: "2rem",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
  },
}));

const STEPS = {
  COMPANY_INFO: 0,
  WORK_CENTERS_INFO: 1,
  EMPLOYEES_INFO: 2,
  CONFIRMATION: 3,
};

function RENTCreate() {
  const classes = useStyles();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const [totalWorkerCount, setTotalWorkerCount] = useState(0);
  const [totalWorkerOnMarchCount, setTotalWorkerOnMarchCount] = useState(0);

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const form = useForm({
    mode: "onChange",
    defaultValues: {
      user: {
        id: "",
        name: "",
        email: "",
        address: "",
        taxIdNumber: "",
        niss: "",
        phoneNumber: "",

        provinceId: "",
        municipalityId: "",
        legalForm: "",

        totalWorkersNumber: "",
        shareCapital: "",
        publicDivisionPercentage: "",
        privateNationalDivisionPercentage: "",
        activityStartYear: null,

        businessPurposeList: [],
      },
    },
  });

  const focusOnFirstError = (errors) => {
    let fieldName = null;
    const firstErrorField = Object.keys(errors)[0];
    
    if (firstErrorField) {
      if (errors[firstErrorField] && typeof errors[firstErrorField] === 'object' && !errors[firstErrorField].message) {
        const nestedField = Object.keys(errors[firstErrorField])[0];
        if (nestedField) {
          fieldName = `${firstErrorField}.${nestedField}`;
        }
      } else {
        fieldName = firstErrorField;
      }

      if (fieldName) {
        setTimeout(() => {
          form.setFocus(fieldName);

          setTimeout(() => {
            let element = document.querySelector(`[name="${fieldName}"]`);
            
            if (!element) {
              element = document.querySelector(`input[name="${fieldName}"], select[name="${fieldName}"]`);
            }

            if (!element) {
              const autocompleteId = `form-dropdown-${fieldName}`;
              const autocompleteElement = document.querySelector(`[data-testid="${autocompleteId}"]`);
              if (autocompleteElement) {
                element = autocompleteElement.querySelector('input');
              }
            }

            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
            }
          }, 100);
        }, 0);
      }
    }
  };

  const { mutateAsync } = useMutation(["createMapRent"], createMapRent, {
    onSuccess: (data) => {
      if (data.succeeded) {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: data.succeeded,
              onClose: data.succeeded ? "/admin/rent/list" : null,
              goBack: true,
              message: data.succeeded ? "Mapa RENT criado com sucesso." : "Ocorreu um erro ao criar o mapa RENT.",
            },
          },
        });

        queryClient.invalidateQueries({
          queryKey: ["getFilteredMapRent"],
        });
      } else {
        dispatch({
          type: DIALOG.SHOW,
          payload: {
            showGenericDialog: true,
            genericDialogData: {
              type: DIALOG.ONE_BUTTON_TYPE,
              isSuccess: false,
              message: "Ocorreu um erro ao criar o mapa RENT.",
            },
          },
        });
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const { steps, activeStep, canGoNext, handleNext, canGoBack, handleBack, handleStep } = useStepper([
    {
      title: "Informações da Empresa",
      subtitle: "Registo de dados da empresa",
    },
    {
      title: "Dados de Centros",
      subtitle: "Registo de dados dos centros de trabalho",
    },
    {
      title: "Dados de Trabalhadores",
      subtitle: "Registo de dados dos trabalhadores",
    },
    {
      title: "Confirmação",
      subtitle: "Confirmação dos dados",
    },
  ]);

  // Função chamada quando há erros de validação
  const onError = (errors) => {
    focusOnFirstError(errors);
  };

  const onSubmit = async (data) => {
    try {
      if (!canGoNext) {
        // eslint-disable-next-line eqeqeq
        if (totalWorkerCount != totalWorkerOnMarchCount) {
          dispatch({
            type: DIALOG.SHOW,
            payload: {
              showGenericDialog: true,
              genericDialogData: {
                type: DIALOG.ONE_BUTTON_TYPE,
                isSuccess: false,
                message:
                  "O número de trabalhadores na última semana de Março (inserido no separador Informações da Empresa) não corresponde ao número de trabalhadores submetidos no separador Dados de Trabalhadores.",
              },
            },
          });
          handleStep(2);
          return;
        } else {
          showDialog(
            "Submeter Mapa Rent",
            "Tem a certeza que deseja submeter o mapa RENT?",
            async () => {
              await mutateAsync({
                ...data.user,
                activityStartYear: data.user.activityStartYear.format("YYYY"),
                employmentCenterId: data.employmentCenterId,
                submissionTypeId: data.submissionTypeId,
                address: data?.user?.address ?? "",
              });
            },
            () => {},
          );

          return;
        }
      }
      handleNext();
    } catch (error) {
      console.error(error);
    }
  };

  const nextLabel = useMemo(() => (!canGoNext ? "Submeter Mapa Rent" : "Seguinte"), [canGoNext]);

  return (
    <FormProvider {...form}>
      <Container maxWidth="lg" className={classes.root}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit, onError)}>
          <LocationHeader
            section="Criar Mapa RENT"
            subsection="Lista de Mapas RENT"
            subsectionLink="/admin/rent/list"
          />

          <header className={classes.header}>
            <Typography
              style={{
                textTransform: "uppercase",
              }}
              color="primary"
              variant="h1"
            >
              Criar Mapa RENT
            </Typography>
          </header>

          <NavigationButtons nextLabel={nextLabel} canGoBack={canGoBack} handleBack={handleBack} />

          <Stepper steps={steps} activeStep={activeStep} showIndex>
            {(step) => {
              switch (step) {
                case STEPS.COMPANY_INFO:
                  return <CompanyInfoTab setTotalWorkerOnMarchCount={setTotalWorkerOnMarchCount} />;
                case STEPS.WORK_CENTERS_INFO:
                  return <WorkCentersInfoTab />;
                case STEPS.EMPLOYEES_INFO:
                  return <EmployeesInfoTab setTotalWorkerCount={setTotalWorkerCount} />;
                case STEPS.CONFIRMATION:
                  return <ConfirmationTab />;
                default:
                  return null;
              }
            }}
          </Stepper>

          <NavigationButtons nextLabel={nextLabel} canGoBack={canGoBack} handleBack={handleBack} showCancel />
        </form>
      </Container>
    </FormProvider>
  );
}

export default function Page() {
  const { data: canSubmitRENT } = useQuery(["getCheckRENTSubmission"], () => getCheckRENTSubmission());

  if (canSubmitRENT && !canSubmitRENT.canSubmit) {
    return <Redirect to="/admin/rent/list" />;
  }

  return (
    <StepProvider>
      <RENTCreate />
    </StepProvider>
  );
}
